// audit.service — orkestrasi Delete Approval (§26 v20). Ladder: SPV+ ajukan → M/O approve.
import { currentUser } from '@core/session';
import { verifyOwnPassword } from '@modules/auth/auth.service';
import { pushNotif } from '@core/notif';
import { logAudit } from '@core/audit';
import * as repo from './audit.repo';

const REQ_ROLES = ['SPV', 'Manager', 'Owner'];         // minimal SPV boleh mengajukan
const APPROVE_ROLES = ['Owner', 'Manager'];            // hanya M/O menyetujui

export type ModeHapus = 'KEEP_FIN' | 'TOTAL';

/**
 * HAPUS TIKET oleh OWNER — langsung, tanpa approval siapa pun (keputusan Apo 28 Jul).
 * Dulu Owner yang mengajukan sendiri terjebak: aturan pemisahan tugas memblok menyetujui permintaan
 * sendiri, padahal Owner-nya cuma satu → tak ada yang bisa memutuskan. Karena Owner memang otoritas
 * tertinggi, ia menghapus langsung; SPV/Manager tetap lewat jalur pengajuan.
 *
 * `password` = password Owner sendiri, dibuktikan ulang sebelum menghapus (Apo: setiap hapus wajib
 * password). Kalau perangkat ditinggal dalam keadaan login, ini yang mencegah orang lain menghapus.
 * Otoritas sesungguhnya tetap di DB: RPC menolak siapa pun yang bukan Owner.
 */
export async function ownerDeleteTicket(no: string, mode: ModeHapus, reason: string, password: string): Promise<ModeHapus> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (u.role !== 'Owner') throw new Error('Hanya Owner yang boleh menghapus transaksi.');
  if (!reason.trim()) throw new Error('Alasan wajib diisi.');
  await verifyOwnPassword(password);
  return repo.ownerDeleteTicketRpc(no, mode, reason.trim());
}

/** Ajukan hapus/void tiket (bukan hard-delete). Sukses→ARCHIVE, aktif→VOID. */
export async function requestDeleteTicket(no: string, reason: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!REQ_ROLES.includes(u.role)) throw new Error('Minimal SPV yang boleh mengajukan hapus.');
  if (!reason.trim()) throw new Error('Alasan wajib diisi.');
  const t = await repo.getTicketMeta(no);
  const isDone = t.status === 'Sudah Diambil' || t.hasil === 'jadi';
  const actionType = isDone ? 'ARCHIVE' : 'VOID';   // transaksi sukses → arsip (bukan hapus)
  const risk = isDone ? 'HIGH' : 'NORMAL';
  await repo.createApproval({ module: 'ticket', ref_id: no, outlet: t.outlet, reason, risk_level: risk, action_type: actionType, before_json: t });
  await pushNotif('SPV', t.outlet, `🗑️ ${u.nama} mengajukan ${actionType} tiket ${no} (${t.custNama}) — perlu approval Manager/Owner.`, no);
  await logAudit({ action: 'DELETE_REQUESTED', module: 'ticket', refId: no, outlet: t.outlet, reason, meta: { actionType, risk } });
}

/** Manager/Owner setujui / tolak. §26 HARDENING (0036): SEMUA guard + apply + audit ATOMIK di RPC
 *  `apply_ticket_approval` (SECURITY DEFINER) = trust-boundary DB. App hanya gate peran (UX) + notif.
 *  (Guard PENDING, separation-of-duties HIGH, re-derive risk dari status kini, set flag trigger → di DB.) */
export async function decideApproval(id: number, decision: 'APPROVE' | 'REJECT', note: string): Promise<void> {
  const u = currentUser();
  if (!u || !APPROVE_ROLES.includes(u.role)) throw new Error('Hanya Manager/Owner yang menyetujui.');
  const a = await repo.getApproval(id); // info untuk notif (RPC = otoritas guard/apply/audit)
  const result = await repo.applyApprovalRpc(id, decision, note); // 'APPLIED' | 'REJECTED' (throw bila SoD/PENDING gagal)
  // Notif best-effort (audit sudah ditulis DI RPC — jangan dobel logAudit).
  if (result === 'REJECTED') await pushNotif('SPV', a.outlet, `🚫 Permintaan ${a.action_type ?? 'hapus'} ${a.ref_id} DITOLAK.`, a.ref_id);
  else await pushNotif('SPV', a.outlet, `✅ ${a.action_type ?? 'Hapus'} ${a.ref_id} disetujui & diterapkan.`, a.ref_id);
}
